import fetch from 'isomorphic-fetch'
import capitalize from 'lodash/capitalize'
import lowerCase from 'lodash/lowerCase'
import { logError } from 'util/error'
import { DAYS } from 'util/format'
import { camelizeKeys, decamelizeKeys } from 'humps'

const lego = window.lego

function ajax(endpoint, method = 'GET', body = null) {
  const headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-CSRF-Token': window.lego.csrf,
  }

  const accessToken = localStorage.getItem('access_token')
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`
  }

  const params = { method, headers: new Headers(headers) }
  if (body) {
    params.body = JSON.stringify(decamelizeKeys(body))
  }

  return fetch(`${lego.config.DOMAIN}${endpoint}`, params)
    .then(response => response.json().then(json => ({ json, response })))
    .then(({ json, response }) => {
      const camelized = { json: camelizeKeys(json), response }
      if (!response.ok) {
        return Promise.reject(camelized)
      }
      return camelized
    })
    .catch(err => {
      const response = err.response
      const message = response
        ? `${method} ${response.url} ${response.status} ${response.statusText}`
        : `${method} ${endpoint} failed to respond`

      logError(message, err)
      throw err
    })
}

// serialize objects into query params
function serialize(body, prefix) {
  return Object.keys(body)
    .map(key => {
      const subkey = Array.isArray(body) ? '' : key
      const param = prefix ? `${prefix}[${subkey}]` : key
      const value = body[key]
      if (value !== null && typeof value === 'object') {
        return serialize(value, param)
      }
      return `${encodeURIComponent(param)}=${encodeURIComponent(value)}`
    })
    .join('&')
}

export function get(endpoint, body) {
  if (body) {
    const separator = endpoint.indexOf('?') > -1 ? '&' : '?'
    const decamelized = decamelizeKeys(body)
    return ajax(`${endpoint}${separator}${serialize(decamelized)}`)
  }

  return ajax(endpoint)
}

export function post(endpoint, body) {
  return ajax(endpoint, 'POST', body)
}

export function put(endpoint, body) {
  return ajax(endpoint, 'PUT', body)
}

export function patch(endpoint, body) {
  return ajax(endpoint, 'PATCH', body)
}

export function del(endpoint) {
  return ajax(endpoint, 'DELETE')
}

function humanize(str) {
  // replace things like "schoolId" with "school"
  if (/Id$/.test(str)) {
    return humanize(str.substring(0, str.length - 2))
  }
  return capitalize(lowerCase(str))
}

function formatError(param, error) {
  if (typeof error === 'string') {
    return `${humanize(param)} ${error}`
  }
  const keys = Object.keys(error)
  return keys
    .reduce((messages, key) => {
      const message = error[key].join(', ')
      if (message) {
        return messages.concat(`${humanize(param)} ${key} ${message}`)
      }
      return messages
    }, [])
    .join(', ')
}

// expects an API response error and optional hash
// of response error codes with corresponding messages
export function apiError(resp, codes) {
  const response = resp || {}

  if (codes && codes[response.code]) {
    return [codes[response.code]]
  }

  if (response.errors) {
    const keys = Object.keys(response.errors)
    return keys.reduce((messages, key) => {
      // if this is an error on an embedded schema
      if (!Array.isArray(response.errors[key])) {
        return apiError({ errors: response.errors[key] })
      }

      const message = response.errors[key]
        .map(error => formatError(key, error))
        .filter(msg => msg)
        .join(', ')

      if (message) {
        return messages.concat(message)
      }
      return messages
    }, [])
  }

  switch (response.code) {
    case 'scheduling_conflict':
      return [
        'One of the times you selected is not available! Please adjust your schedule and try again.',
      ]
    case 'enrollment_is_before_birthday':
      return ["The enrollment start must be after your child's birth date"]
    case 'invalid_schedule': {
      let msg = 'That schedule does not work'
      const numDays = response.meta && response.meta.validDaysPerWeek
      if (numDays) {
        const options = numDays.join(', ').replace(/,\s*/, ' or ')
        msg = `${msg}, please choose ${options} days per week`
      }
      const validAges = response.meta && response.meta.validAges
      if (validAges) {
        const ageRange = validAges.reduce((r, ages) => {
          let min = r[0]
          let max = r[1]
          if (typeof min === 'undefined' || ages[0] < min) {
            min = ages[0]
          }
          if (typeof max === 'undefined' || ages[1] > max) {
            max = ages[1]
          }
          return [min, max]
        }, [])
        msg = `${msg}, child must be between ${ageRange[0]} and ${ageRange[1]} months old`
      }
      const validWeekdays = response.meta && response.meta.validWeekdays
      if (validWeekdays) {
        const options = validWeekdays
          .reduce((opts, weekdays) => {
            const days = weekdays
              .sort()
              .map(d => DAYS[d])
              .join(', ')
            return opts.indexOf(days) > -1 ? opts : opts.concat(days)
          }, [])
          .join('; ')
          .replace(/;\s([^;]+)$/, '; or $1')
        msg = `${msg}, please choose ${options}`
      }
      return [msg]
    }
    default:
      return ['There was an error with your submission. Please try again!']
  }
}
